import { useState, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { EcommerceBannerPlacement, IEcommerceQuestionViewProps } from '../types.ts'
import style from './Kroger.module.scss'
import Header from './components/Header.tsx'
import Main from './components/Main.tsx'
import Product from './components/Product.tsx'
import Cart from './components/Cart.tsx'
import Footer from './components/Footer.tsx'
import CartRail from './components/CartRail.tsx'
import useLoadScriptInFrame from '../hooks/useLoadScriptInFrame.ts'
import useScrollMemory from '../hooks/useScrollMemory.ts'

const KrogerView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const na = schema.answers.find(answer => answer.na)
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v * cur.price), 0)
  ]
  const selectedAnswer = response.answers.find(r => r.selected)
  const selectedId = selectedAnswer?.id
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')

  const [showCart, setShowCart] = useState<boolean>(false)
  const [needHint, setNeedHint] = useState(true)

  const session = useSession()
  const timeline = useTimeline()
  const theme = useTheme()
  const viewport = useViewport()
  const { containerRef, registerCardRef, findCenterElement, restorePosition, scrollTop } = useScrollMemory()
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  useEffect(() => {
    if (!showCart && !selectedId) {
      restorePosition()
    }
  }, [showCart, selectedId, restorePosition])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    if (selectedId) {
      timeline.log({ type: 'close_popup', ref: selectedId })
    }
    onUpdateResponse({
      answers: response.answers.map(r => ({ ...r, selected: false })),
      clicked: response.clicked
    })
  }

  const onItemSelect = (index: number, bannerPlacement?: EcommerceBannerPlacement) => {
    if (bannerPlacement) {
      timeline.log({
        type: 'banner_click',
        ref: response.answers[index].id,
        context: { placement: bannerPlacement }
      })
    }
    scrollTop()
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    const answer = response.answers[index]
    answer.selected = true
    timeline.log({ target: '0,0', type: 'open_popup', ref: answer.id })
    onUpdateResponse(response)
  }

  const onItemClose = () => {
    if (!selectedAnswer) return
    timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
    selectedAnswer.selected = false
    onUpdateResponse(response)
  }

  const onCartOpen = () => {
    scrollTop()
    if (selectedAnswer) {
      timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
      selectedAnswer.selected = false
      onUpdateResponse(response)
    }
    setShowCart(true)
    timeline.log({ type: 'open_cart' })
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({ type: 'close_cart' })
  }

  const onAddToCart = (index: number, qnt: number, inCarousel = false) => {
    const answer = response.answers[index]
    answer.v = Math.max(0, answer.v + qnt)
    if (inCarousel || (!showCart && !selectedId)) {
      timeline.log({ target: '0,0', type: 'open_popup', ref: answer.id })
      timeline.log({ type: 'add_to_cart', ref: answer.id, target: '1' })
      timeline.log({ type: 'close_popup', ref: answer.id })
    } else if (showCart) {
      timeline.log({ type: 'edit_in_cart', ref: answer.id, target: answer.v.toString() })
    } else {
      timeline.log({ type: 'add_to_cart', ref: answer.id, target: answer.v.toString() })
      if (findability) {
        timeline.log({ type: 'close_popup', ref: answer.id })
      }
    }
    onUpdateResponse(response)
  }

  const onDeleteFromCart = (index: number) => {
    const answer = response.answers[index]
    answer.v = 0
    timeline.log({ type: 'delete_from_cart', ref: answer.id })
    onUpdateResponse(response)
  }

  const onHomeClick = () => {
    showCart ? onCartClose() : onItemClose()
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse && naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        onScroll={findCenterElement}
        ref={containerRef}
      >
        <Header
          schema={schema}
          total={totals[0]}
          findability={findability}
          clearSelection={clearSelection}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
        />
        {selectedId ? (
          <Product
            answer={schema.answers.find(answer => answer.id === selectedId)}
            schema={schema}
            response={response}
            findability={findability}
            cartTotal={totals[0]}
            onItemClose={onItemClose}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        ) : showCart ? (
          <Cart
            response={response}
            schema={schema}
            totals={totals}
            onCartClose={onCartClose}
            onAddToCart={onAddToCart}
            onDeleteFromCart={onDeleteFromCart}
            onItemSelect={onItemSelect}
            onUpdateResponse={onUpdateResponse}
            checkout={checkout}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            schema={schema}
            response={response}
            findability={findability}
            cartTotal={totals[0]}
            registerCardRef={registerCardRef}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        )}
        <Footer
          findability={findability}
          showCart={showCart}
          showMain={!showCart && !selectedId}
          total={totals[0]}
          onHomeClick={onHomeClick}
          onCartOpen={onCartOpen}
          showHint={() => setNeedHint(true)}
          closeSimulation={setClosed}
        />
        {!findability && !showCart && !viewport.mobile && (
          <CartRail
            schema={schema}
            response={response}
            total={totals[1]}
            selectedId={selectedId}
            onItemSelect={onItemSelect}
            onItemClose={onItemClose}
            onGoToCart={onCartOpen}
            onQtyChange={onAddToCart}
          />
        )}
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default KrogerView
